/**
 * employeeEngineConfig.tsx — config silnika formatek dla kartoteki Pracownik
 * (keystone unifikacja-formatek — mirror `contractorEngineConfig.tsx` / `userEngineConfig.tsx`).
 *
 * Pola core pracownika (poza PESEL/numer dowodu — patrz niżej) dostają custom renderer —
 * zero natywnej delegacji do `ExtFieldValueInput` (mirror kontrahenta/użytkownika), więc
 * gałąź natywna silnika jest martwa dla tej kartoteki — `labelSource`/`coerceEmptyToNull`
 * zostają kosmetyczne.
 *
 * Etykiety: `ctx.field.name` (definicja pola z `GET /system-modules/employees/fields`,
 * DB-driven), NIE i18n — zachowanie 1:1 z `employeeCoreRenderers.tsx` (przed migracją,
 * plik usunięty razem z rejestrem `EMPLOYEE_CORE_RENDERERS`, silnik go zastępuje).
 *
 * 🔴 PESEL / numer dowodu (`pesel`/`id_document_number`) NIE wchodzą do tego configu — PII
 * szyfrowane, CELOWO ABSENT z katalogu `system_module_fields` (`apps/api/lib/seed-system-modules.ts`,
 * moduł `employees`) i z server-side denylisty (`lib/dictionaries/sensitive-guard.ts`,
 * fail-closed, DENYLISTED_EXACT_SLUGS). Zostają hardkodowaną sekcją `SensitiveSection` w
 * `EmployeeDataTab.tsx`, renderowaną OUTSIDE silnika (mirror `tags`/`pkd_codes` kontrahenta).
 * NIE dodawaj ich tutaj ani do `EMPLOYEE_CORE_FIELD_SLUGS` — złamałoby to podwójną bramkę
 * bezpieczeństwa (§4.4 CLAUDE.md, permission `hr.employees.read_pesel`).
 *
 * F24 Pakiet 0 (struktura organizacyjna) — trzy zmiany kontraktu:
 *  - `department_id` → `org_unit_id`: kierownik firmy ma teraz DRZEWO jednostek (dział/pion/
 *    oddział), nie płaską listę działów — `orgUnitIdRenderer` renderuje `ctx.extra.orgUnits`
 *    jako opcje WCIĘTE wg głębokości (`buildIndentedOrgUnitOptions`, `lib/use-tenant-people.ts`).
 *  - `position` (wolny tekst) → `position_code`: kod pozycji słownika `employee_positions`
 *    (globalny katalog ITE, startuje PUSTY — tenant dopisuje pozycje w Ustawienia → Słowniki).
 *    Custom renderer (nie natywna delegacja) — dokłada podpowiedź „słownik pusty" gdy
 *    `activeItems.length === 0`, natywny `ExtFieldValueInput` by tego nie pokazał.
 *  - `structure_level` USUNIĘTE (pole bez semantyki, zero konsumentów) — renderer i slug skasowane.
 *  - `manager_employee_id` jest teraz NADPISANIEM przełożonego (wyjątek od drzewa jednostek;
 *    puste = przełożony wynika z kierownika jednostki) — etykieta „Przełożony (nadpisanie)"
 *    przychodzi z katalogu (seed backendu), renderer dokłada tylko podpowiedź `description`.
 *
 * Quirki portowane 1:1 z `employeeCoreRenderers.tsx`:
 *  - `company_id`: disabled TYLKO gdy `!ctx.editable` (w odróżnieniu od kontrahenta,
 *    pracownik NIE ma osobnego flow transferu właścicielstwa firmy — pole jest edytowalne
 *    po zapisaniu rekordu tak samo jak reszta pól).
 *  - `org_unit_id`: `ctx.extra.orgUnits` filtrowane po `parentId === ctx.form.companyId`
 *    (podwójny filtr — `EmployeeDetail.tsx` już zawęża zapytanie po companyId, renderer
 *    filtruje defensywnie tak samo jak przed migracją; nazwa pola `parentId` w
 *    `EmployeeLookupOption` niesie tu `companyId`, nie `org_units.parentId`).
 *  - `manager_employee_id`: jak wyżej + wyklucza `ctx.extra.currentEmployeeId` (edytowany
 *    rekord nie może być swoim własnym przełożonym).
 *  - `groups`/`category_id`: jak w Kontrahentach/Użytkownikach (GroupsInput /
 *    RecordCategoryPicker). `groups` DOPISANE do `EMPLOYEE_CORE_FIELD_SLUGS` — poprzednio
 *    go tam brakowało mimo że `EMPLOYEE_CORE_RENDERERS` je obsługiwało (dawny dispatch po
 *    `hasEmployeeCoreRenderer`, NIE po tej mapie); silnik buduje `fields` WYŁĄCZNIE z tej
 *    mapy, więc bez wpisu `groups` spadłoby na amber fallback „unsupported field" — mirror
 *    identycznego dopisania przy migracji kontrahenta (`CONTRACTOR_CORE_FIELD_SLUGS`).
 *
 * Kanał `extra` (EmployeeExtra) niesie listy lookupów (companies/orgUnits/managers) +
 * `currentEmployeeId` — wcześniej żyły w `EmployeeDataTab` (`useCompanies`/
 * `useOrgUnitsForCompany`/`useEmployeesList`), teraz w `EmployeeDetail.tsx` (silnik
 * wymaga JEDNEGO kierunku przepływu: strona → runtime.extra → renderer).
 *
 * `is_active` — goły `<input type="checkbox">` zmigrowany na `<Checkbox>` (§4.12) przy domknięciu
 * weryfikacji migracji; wpis `no-restricted-syntax` wyzerowany w `eslint-suppressions.json`.
 * (Precedensy `contractorEngineConfig.tsx` / `userEngineConfig.tsx` zostawiły swoje surowe
 * checkboxy — osobny dług §4.12, poza zakresem tego PR.)
 */

import { useTranslation } from 'react-i18next';
import { EMPLOYEE_ID_DOCUMENT_TYPES, EMPLOYMENT_TYPES } from '@saas/shared';

import { Checkbox } from '@/components/Checkbox';
import { CoreInput, type CoreInputProps } from '@/components/CoreInput';
import { Dropdown, type DropdownOption } from '@/components/Dropdown';
import { GroupsInput } from '@/components/groups/GroupsInput';
import { Field } from '@/components/kartoteki/CoreFieldLayout';
import type {
  CoreFieldConfig,
  FieldRenderContext,
  FieldRenderer,
  KartotekaEngineConfig,
} from '@/components/kartoteki/engine/engine-config';
import { RecordCategoryPicker } from '@/components/record-categories/RecordCategoryPicker';
import { CompanyPicker } from '@/components/ui/CompanyPicker';
import { formatDictionaryOptionLabel, useDictionary } from '@/hooks/useDictionary';
import { employeeIdDocumentTypeLabel, employmentTypeLabel } from '@/lib/core-field-option-labels';
import { cn } from '@/lib/utils';

import { EMPLOYEE_CORE_FIELD_SLUGS, type EmployeeFormData } from './EmployeeDataTab';

/** Opcja lookupu (firma/jednostka/przełożony) — `id` + etykieta + opc. filtr firmy (`parentId`). */
export interface EmployeeLookupOption {
  id: string;
  label: string;
  /** Opc. filtr (np. jednostki firmy A nie powinny się pojawić gdy firma B wybrana). */
  parentId?: string | null;
}

/**
 * Stan runtime specyficzny dla pracownika, niesiony przez `FieldRenderContext.extra` /
 * `KartotekaRenderRuntime.extra` (silnik go nie interpretuje — patrz engine-config.ts).
 * `EmployeeDetail.tsx` woła `useCompanies()`/`useOrgUnitsForCompany()`/`useEmployeesList()`
 * (przeniesione z `EmployeeDataTab`) i mapuje na te listy.
 */
export interface EmployeeExtra {
  companies: EmployeeLookupOption[];
  /**
   * Wszystkie jednostki organizacyjne (dział/pion/oddział), filter po `parentId===companyId`
   * w renderze. Etykieta już WCIĘTA wg głębokości drzewa (`buildIndentedOrgUnitOptions`,
   * `lib/use-tenant-people.ts`) — `EmployeeDetail.tsx` buduje tę listę stąd, nie z surowego
   * `OrgUnitItem[]`.
   */
  orgUnits: EmployeeLookupOption[];
  /** Wszyscy pracownicy (filter po `parentId===companyId` w renderze) — manager picker. */
  managers: EmployeeLookupOption[];
  /** Aktualnie edytowany pracownik (do wykluczenia z listy „przełożony"). `null` w create. */
  currentEmployeeId: string | null;
}

type Ctx = FieldRenderContext<EmployeeFormData, EmployeeExtra>;

/** Ramka błędu dla `Dropdown` (bez własnego propa `invalid`) — mirror `userEngineConfig.tsx`. */
const DROPDOWN_INVALID_CLASS = 'border-red-500 ring-1 ring-red-500';

function TextInput(props: Omit<CoreInputProps, 'variant'>) {
  return <CoreInput {...props} />;
}

function LookupSelect({
  value,
  onChange,
  options,
  editable,
  required,
  testId,
}: {
  value: string | null;
  onChange: (v: string | null) => void;
  options: EmployeeLookupOption[];
  editable: boolean;
  required?: boolean;
  testId?: string;
}) {
  const { t } = useTranslation();
  const dropdownOptions: DropdownOption[] = [
    ...(!required ? [{ value: '', label: t('common.notSet') }] : []),
    ...(required && !value ? [{ value: '', label: t('common.selectPlaceholder') }] : []),
    ...options.map((o) => ({ value: o.id, label: o.label })),
  ];
  return (
    <Dropdown
      value={value ?? ''}
      onChange={(v) => onChange(v || null)}
      disabled={!editable}
      data-testid={testId}
      options={dropdownOptions}
    />
  );
}

function EmploymentTypeSelect({ ctx }: { ctx: Ctx }) {
  const { t } = useTranslation();
  const options: DropdownOption[] = [
    { value: '', label: t('common.notSet') },
    ...EMPLOYMENT_TYPES.map((v) => ({
      value: v,
      label: employmentTypeLabel(v, t),
    })),
  ];
  return (
    <Dropdown
      value={ctx.form.employmentType ?? ''}
      onChange={(v) =>
        ctx.setField({ employmentType: (v || null) as EmployeeFormData['employmentType'] })
      }
      disabled={!ctx.editable}
      data-testid="employee-field-employmentType"
      options={options}
      className={ctx.hasError?.('employmentType') ? DROPDOWN_INVALID_CLASS : undefined}
    />
  );
}

function IdDocTypeSelect({ ctx }: { ctx: Ctx }) {
  const { t } = useTranslation();
  const options: DropdownOption[] = [
    { value: '', label: t('common.notSet') },
    ...EMPLOYEE_ID_DOCUMENT_TYPES.map((v) => ({
      value: v,
      label: employeeIdDocumentTypeLabel(v, t),
    })),
  ];
  return (
    <Dropdown
      value={ctx.form.idDocumentType ?? ''}
      onChange={(v) =>
        ctx.setField({ idDocumentType: (v || null) as EmployeeFormData['idDocumentType'] })
      }
      disabled={!ctx.editable}
      data-testid="employee-field-idDocumentType"
      options={options}
      className={ctx.hasError?.('idDocumentType') ? DROPDOWN_INVALID_CLASS : undefined}
    />
  );
}

// ─── Ground-truth fieldType per slug (z seeda, apps/api/lib/seed-system-modules.ts
// → employees.coreFields) — używany WYŁĄCZNIE przez fallback statyczny silnika (403 na
// metadanych modułu). Renderer i tak owns widget. ───────────────────────────────
const EMPLOYEE_FIELD_TYPES: Record<string, string> = {
  // Wielokrotny wybór ze słownika (kolumna TEXT[] kodów) — patrz `groupsRenderer`.
  groups: 'multiselect',
  // Pojedynczy wybór z realnej tabeli `record_categories` — patrz `categoryRenderer`.
  category_id: 'select',
  first_name: 'varchar',
  last_name: 'varchar',
  middle_name: 'varchar',
  birth_date: 'date',
  tax_id: 'varchar',
  id_document_type: 'select',
  company_id: 'reference',
  org_unit_id: 'reference',
  // Kod pozycji słownika `employee_positions` (F24 Pakiet 0, dawniej wolny tekst `position`).
  position_code: 'select',
  specialization: 'varchar',
  employment_type: 'select',
  employment_start_date: 'date',
  employment_end_date: 'date',
  internal_code: 'varchar',
  manager_employee_id: 'reference',
  phone: 'varchar',
  mobile: 'varchar',
  work_email: 'email',
  is_active: 'boolean',
  // Integracja Symfonia (F18/FK) — READ-ONLY (silnik wymusza editable=false z flagi katalogu
  // `read_only`). Pozycja liczbowa + status po stronie FK; patrz symfonia*Renderer niżej.
  symfonia_external_id: 'varchar',
  symfonia_position: 'integer',
  symfonia_active: 'boolean',
};

// ─── Mapa renderers (slug → FieldRenderer), portowane 1:1 z employeeCoreRenderers.tsx ──

/**
 * Grupy ze słownika `employee_groups` — wielokrotny wybór. Renderer TUTAJ, żeby o miejscu
 * pola na formatce decydował LAYOUT z buildera (wzorzec 1:1 z kontrahentami/użytkownikami).
 */
const groupsRenderer: FieldRenderer<EmployeeFormData, EmployeeExtra> = (ctx) => (
  <Field label={ctx.field.name} colSpan={2}>
    <GroupsInput
      dictionaryCode="employee_groups"
      value={ctx.form.groups}
      onChange={(next) => ctx.setField({ groups: next })}
      disabled={!ctx.editable}
      data-testid="employee-groups"
    />
  </Field>
);

/**
 * Kategoria z realnej tabeli `record_categories` (kartoteka_key='employees') — pojedynczy
 * wybór, DYSTYNKTYWNY od `groupsRenderer` (wiele, słownik). `RecordCategoryPicker` rysuje
 * własny `<Field>`, więc renderer nie owija go drugi raz. Migracja 0128 — patrz
 * `apps/api/lib/records/categories.ts`.
 */
const categoryRenderer: FieldRenderer<EmployeeFormData, EmployeeExtra> = (ctx) => (
  <RecordCategoryPicker
    kartotekaKey="employees"
    value={ctx.form.categoryId}
    onChange={(next) => ctx.setField({ categoryId: next })}
    editable={ctx.editable}
    testId="employee-category"
  />
);

const firstNameRenderer: FieldRenderer<EmployeeFormData, EmployeeExtra> = (ctx) => (
  <Field label={ctx.field.name} required={ctx.field.isRequired} error={ctx.getError?.('firstName')}>
    <TextInput
      testId="employee-field-firstName"
      value={ctx.form.firstName}
      onChange={(v) => ctx.setField({ firstName: v })}
      disabled={!ctx.editable}
      invalid={ctx.hasError?.('firstName')}
    />
  </Field>
);

const lastNameRenderer: FieldRenderer<EmployeeFormData, EmployeeExtra> = (ctx) => (
  <Field label={ctx.field.name} required={ctx.field.isRequired} error={ctx.getError?.('lastName')}>
    <TextInput
      testId="employee-field-lastName"
      value={ctx.form.lastName}
      onChange={(v) => ctx.setField({ lastName: v })}
      disabled={!ctx.editable}
      invalid={ctx.hasError?.('lastName')}
    />
  </Field>
);

const middleNameRenderer: FieldRenderer<EmployeeFormData, EmployeeExtra> = (ctx) => (
  <Field
    label={ctx.field.name}
    required={ctx.field.isRequired}
    error={ctx.getError?.('middleName')}
  >
    <TextInput
      value={ctx.form.middleName ?? ''}
      onChange={(v) => ctx.setField({ middleName: v || null })}
      disabled={!ctx.editable}
      invalid={ctx.hasError?.('middleName')}
    />
  </Field>
);

const birthDateRenderer: FieldRenderer<EmployeeFormData, EmployeeExtra> = (ctx) => (
  <Field label={ctx.field.name} required={ctx.field.isRequired} error={ctx.getError?.('birthDate')}>
    <TextInput
      type="date"
      value={ctx.form.birthDate ?? ''}
      onChange={(v) => ctx.setField({ birthDate: v || null })}
      disabled={!ctx.editable}
      invalid={ctx.hasError?.('birthDate')}
    />
  </Field>
);

const taxIdRenderer: FieldRenderer<EmployeeFormData, EmployeeExtra> = (ctx) => (
  <Field label={ctx.field.name} required={ctx.field.isRequired} error={ctx.getError?.('taxId')}>
    <TextInput
      value={ctx.form.taxId ?? ''}
      onChange={(v) => ctx.setField({ taxId: v || null })}
      disabled={!ctx.editable}
      invalid={ctx.hasError?.('taxId')}
    />
  </Field>
);

const idDocumentTypeRenderer: FieldRenderer<EmployeeFormData, EmployeeExtra> = (ctx) => (
  <Field
    label={ctx.field.name}
    required={ctx.field.isRequired}
    error={ctx.getError?.('idDocumentType')}
  >
    <IdDocTypeSelect ctx={ctx} />
  </Field>
);

/**
 * Core invariant (bez tworzenia rekordu bez jawnie wybranej konkretnej firmy) — mirror
 * `CompanyPicker` kontrahenta. TRAP FIX: `disabled={!ctx.editable}` — pracownik NIE ma
 * osobnego flow transferu właścicielstwa (kontrahent ma, stąd jego `!ctx.isCreate`); firma
 * jest edytowalna po zapisaniu rekordu tak samo jak reszta pól core.
 */
const companyIdRenderer: FieldRenderer<EmployeeFormData, EmployeeExtra> = (ctx) => (
  <Field label={ctx.field.name} required={ctx.field.isRequired} error={ctx.getError?.('companyId')}>
    <CompanyPicker
      value={ctx.form.companyId || null}
      onChange={(v) => ctx.setField({ companyId: v ?? '' })}
      companies={(ctx.extra?.companies ?? []).map((c) => ({ id: c.id, legalName: c.label }))}
      disabled={!ctx.editable}
      required={ctx.field.isRequired}
      testId="employee-field-companyId"
    />
  </Field>
);

/** Picker jednostki organizacyjnej — opcje już WCIĘTE wg głębokości (`ctx.extra.orgUnits`). */
const orgUnitIdRenderer: FieldRenderer<EmployeeFormData, EmployeeExtra> = (ctx) => (
  <Field label={ctx.field.name} required={ctx.field.isRequired} error={ctx.getError?.('orgUnitId')}>
    <LookupSelect
      value={ctx.form.orgUnitId}
      onChange={(v) => ctx.setField({ orgUnitId: v })}
      options={(ctx.extra?.orgUnits ?? []).filter(
        (u) => !ctx.form.companyId || u.parentId === ctx.form.companyId,
      )}
      editable={ctx.editable}
      testId="employee-field-orgUnitId"
    />
  </Field>
);

/**
 * Kod pozycji ze słownika `employee_positions` (F24 Pakiet 0, dawniej wolny tekst `position`).
 * Custom renderer (nie natywna delegacja `ExtFieldValueInput`) — jedyna różnica: podpowiedź
 * „słownik pusty, dodaj pozycje w Ustawieniach" gdy `activeItems.length === 0` (katalog ITE
 * startuje pusty, tenant dopisuje własne pozycje jako rozszerzenia słownika globalnego).
 */
function PositionCodeSelect({ ctx }: { ctx: Ctx }) {
  const { t } = useTranslation();
  const { dictionary, activeItems } = useDictionary('employee_positions');
  const options: DropdownOption[] = [
    { value: '', label: t('common.notSet') },
    ...activeItems.map((item) => ({
      value: item.code,
      label: dictionary ? formatDictionaryOptionLabel(item, dictionary.displayMode) : item.code,
    })),
  ];
  return (
    <>
      <Dropdown
        value={ctx.form.positionCode ?? ''}
        onChange={(v) => ctx.setField({ positionCode: v || null })}
        disabled={!ctx.editable}
        data-testid="employee-field-positionCode"
        options={options}
        className={ctx.hasError?.('positionCode') ? DROPDOWN_INVALID_CLASS : undefined}
      />
      {dictionary && activeItems.length === 0 && (
        <p
          className="mt-1 text-xs text-text-tertiary"
          data-testid="employee-field-positionCode-empty-hint"
        >
          {t('tenant.employees.field.positionCodeEmptyHint')}
        </p>
      )}
    </>
  );
}

const positionCodeRenderer: FieldRenderer<EmployeeFormData, EmployeeExtra> = (ctx) => (
  <Field
    label={ctx.field.name}
    required={ctx.field.isRequired}
    error={ctx.getError?.('positionCode')}
  >
    <PositionCodeSelect ctx={ctx} />
  </Field>
);

const specializationRenderer: FieldRenderer<EmployeeFormData, EmployeeExtra> = (ctx) => (
  <Field
    label={ctx.field.name}
    required={ctx.field.isRequired}
    error={ctx.getError?.('specialization')}
  >
    <TextInput
      value={ctx.form.specialization ?? ''}
      onChange={(v) => ctx.setField({ specialization: v || null })}
      disabled={!ctx.editable}
      invalid={ctx.hasError?.('specialization')}
    />
  </Field>
);

const employmentTypeRenderer: FieldRenderer<EmployeeFormData, EmployeeExtra> = (ctx) => (
  <Field
    label={ctx.field.name}
    required={ctx.field.isRequired}
    error={ctx.getError?.('employmentType')}
  >
    <EmploymentTypeSelect ctx={ctx} />
  </Field>
);

const employmentStartDateRenderer: FieldRenderer<EmployeeFormData, EmployeeExtra> = (ctx) => (
  <Field
    label={ctx.field.name}
    required={ctx.field.isRequired}
    error={ctx.getError?.('employmentStartDate')}
  >
    <TextInput
      type="date"
      value={ctx.form.employmentStartDate ?? ''}
      onChange={(v) => ctx.setField({ employmentStartDate: v || null })}
      disabled={!ctx.editable}
      invalid={ctx.hasError?.('employmentStartDate')}
    />
  </Field>
);

const employmentEndDateRenderer: FieldRenderer<EmployeeFormData, EmployeeExtra> = (ctx) => (
  <Field
    label={ctx.field.name}
    required={ctx.field.isRequired}
    error={ctx.getError?.('employmentEndDate')}
  >
    <TextInput
      type="date"
      value={ctx.form.employmentEndDate ?? ''}
      onChange={(v) => ctx.setField({ employmentEndDate: v || null })}
      disabled={!ctx.editable}
      invalid={ctx.hasError?.('employmentEndDate')}
    />
  </Field>
);

const internalCodeRenderer: FieldRenderer<EmployeeFormData, EmployeeExtra> = (ctx) => (
  <Field
    label={ctx.field.name}
    required={ctx.field.isRequired}
    error={ctx.getError?.('internalCode')}
  >
    <TextInput
      value={ctx.form.internalCode ?? ''}
      onChange={(v) => ctx.setField({ internalCode: v || null })}
      disabled={!ctx.editable}
      invalid={ctx.hasError?.('internalCode')}
    />
  </Field>
);

/**
 * Nadpisanie przełożonego (F24 Pakiet 0) — wyklucza `ctx.extra.currentEmployeeId` (edytowany
 * rekord nie może być swoim przełożonym; serwer i tak odrzuci to jako `MANAGER_SELF_REFERENCE`,
 * filtr tu jest tylko UX). Puste = przełożony wynika z kierownika jednostki organizacyjnej —
 * `description` niesie tę podpowiedź (etykieta pola „Przełożony (nadpisanie)" z katalogu).
 */
const managerEmployeeIdRenderer: FieldRenderer<EmployeeFormData, EmployeeExtra> = (ctx) => (
  <Field
    label={ctx.field.name}
    required={ctx.field.isRequired}
    description={ctx.t('tenant.employees.field.managerOverrideHint')}
    error={ctx.getError?.('managerEmployeeId')}
  >
    <LookupSelect
      value={ctx.form.managerEmployeeId}
      onChange={(v) => ctx.setField({ managerEmployeeId: v })}
      options={(ctx.extra?.managers ?? []).filter(
        (m) =>
          m.id !== ctx.extra?.currentEmployeeId &&
          (!ctx.form.companyId || m.parentId === ctx.form.companyId),
      )}
      editable={ctx.editable}
      testId="employee-field-managerEmployeeId"
    />
  </Field>
);

const phoneRenderer: FieldRenderer<EmployeeFormData, EmployeeExtra> = (ctx) => (
  <Field label={ctx.field.name} required={ctx.field.isRequired} error={ctx.getError?.('phone')}>
    <TextInput
      value={ctx.form.phone ?? ''}
      onChange={(v) => ctx.setField({ phone: v || null })}
      disabled={!ctx.editable}
      invalid={ctx.hasError?.('phone')}
    />
  </Field>
);

const mobileRenderer: FieldRenderer<EmployeeFormData, EmployeeExtra> = (ctx) => (
  <Field label={ctx.field.name} required={ctx.field.isRequired} error={ctx.getError?.('mobile')}>
    <TextInput
      value={ctx.form.mobile ?? ''}
      onChange={(v) => ctx.setField({ mobile: v || null })}
      disabled={!ctx.editable}
      invalid={ctx.hasError?.('mobile')}
    />
  </Field>
);

const workEmailRenderer: FieldRenderer<EmployeeFormData, EmployeeExtra> = (ctx) => (
  <Field label={ctx.field.name} required={ctx.field.isRequired} error={ctx.getError?.('workEmail')}>
    <TextInput
      type="email"
      value={ctx.form.workEmail ?? ''}
      onChange={(v) => ctx.setField({ workEmail: v || null })}
      disabled={!ctx.editable}
      invalid={ctx.hasError?.('workEmail')}
    />
  </Field>
);

// `is_active` — prymityw `<Checkbox>` (§4.12), patrz komentarz nagłówka pliku.
const isActiveRenderer: FieldRenderer<EmployeeFormData, EmployeeExtra> = (ctx) => (
  <Field label={ctx.field.name}>
    <Checkbox
      checked={ctx.form.isActive}
      disabled={!ctx.editable}
      onChange={(e) => ctx.setField({ isActive: e.target.checked })}
      data-testid="employee-field-isActive"
    />
  </Field>
);

// ─── Integracja Symfonia (F18/FK) — pola READ-ONLY z synchronizacji ───────────────
// Portowane z usuniętego `employeeCoreRenderers.tsx` na silnik. Rekord pracownika jest per
// firma, więc pozycja/status są naturalnie per firma (bez doklejania nazwy firmy jak u
// kontrahenta). Silnik wymusza editable=false z flagi katalogu `read_only`.

/** Status Symfonii (FK `Active`) jako badge — READ-ONLY. Etykiety per stan. ODRĘBNE od `is_active`. */
function SymfoniaActiveBadge({ active }: { active: boolean | null }) {
  const { t } = useTranslation();
  const label =
    active == null
      ? t('tenant.employees.detail.symfonia.unknown')
      : active
        ? t('tenant.employees.detail.symfonia.active')
        : t('tenant.employees.detail.symfonia.blocked');
  return (
    <span
      className={cn(
        'inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium',
        active === false
          ? 'bg-red-500/10 text-red-700 dark:text-red-400'
          : active
            ? 'bg-green-500/10 text-green-700 dark:text-green-400'
            : 'bg-bg-tertiary text-text-tertiary',
      )}
      data-testid="employee-field-symfoniaActive"
    >
      {label}
    </span>
  );
}

// ID rekordu w Symfonii (external_id) — READ-ONLY, ODRĘBNE od Pozycji (portowane z sesji pracowników).
const symfoniaExternalIdRenderer: FieldRenderer<EmployeeFormData, EmployeeExtra> = (ctx) => (
  <Field label={ctx.field.name}>
    <TextInput
      testId="employee-field-symfoniaExternalId"
      value={ctx.form.symfoniaExternalId ?? ''}
      onChange={() => {}}
      disabled={!ctx.editable}
    />
  </Field>
);

const symfoniaPositionRenderer: FieldRenderer<EmployeeFormData, EmployeeExtra> = (ctx) => (
  <Field label={ctx.field.name}>
    <TextInput
      testId="employee-field-symfoniaPosition"
      value={ctx.form.symfoniaPosition !== null ? String(ctx.form.symfoniaPosition) : ''}
      onChange={() => {}}
      disabled={!ctx.editable}
    />
  </Field>
);

const symfoniaActiveRenderer: FieldRenderer<EmployeeFormData, EmployeeExtra> = (ctx) => (
  <Field label={ctx.field.name}>
    <SymfoniaActiveBadge active={ctx.form.symfoniaActive} />
  </Field>
);

const EMPLOYEE_RENDERERS: Record<string, FieldRenderer<EmployeeFormData, EmployeeExtra>> = {
  groups: groupsRenderer,
  category_id: categoryRenderer,
  symfonia_external_id: symfoniaExternalIdRenderer,
  symfonia_position: symfoniaPositionRenderer,
  symfonia_active: symfoniaActiveRenderer,
  first_name: firstNameRenderer,
  last_name: lastNameRenderer,
  middle_name: middleNameRenderer,
  birth_date: birthDateRenderer,
  tax_id: taxIdRenderer,
  id_document_type: idDocumentTypeRenderer,
  company_id: companyIdRenderer,
  org_unit_id: orgUnitIdRenderer,
  position_code: positionCodeRenderer,
  specialization: specializationRenderer,
  employment_type: employmentTypeRenderer,
  employment_start_date: employmentStartDateRenderer,
  employment_end_date: employmentEndDateRenderer,
  internal_code: internalCodeRenderer,
  manager_employee_id: managerEmployeeIdRenderer,
  phone: phoneRenderer,
  mobile: mobileRenderer,
  work_email: workEmailRenderer,
  is_active: isActiveRenderer,
};

const fields: Record<string, CoreFieldConfig<EmployeeFormData, EmployeeExtra>> = {};
for (const slug of Object.keys(EMPLOYEE_CORE_FIELD_SLUGS)) {
  const stateKey = EMPLOYEE_CORE_FIELD_SLUGS[slug]!;
  fields[slug] = {
    stateKey,
    fieldType: EMPLOYEE_FIELD_TYPES[slug] ?? 'varchar',
    renderer: EMPLOYEE_RENDERERS[slug]!,
    // Jedyne pole z colSpan=2 (rysuje go zresztą samo na swoim <Field> — wpis tu jest
    // kosmetyczny, patrz komentarz nagłówka pliku, gałąź natywna silnika jest martwa
    // dla tej kartoteki).
    ...(slug === 'groups' ? { colSpan: 2 as const } : {}),
  };
}

export const employeeEngineConfig: KartotekaEngineConfig<EmployeeFormData, EmployeeExtra> = {
  moduleSlug: 'employees',
  // LOAD-BEARING: engine's ext delegation emits `${testIdPrefix}-${slug}` — testid
  // `employee-ext-<slug>` (zachowanie 1:1 z przed-silnikowym EmployeeDataTab, wykorzystywane
  // w EmployeeDetail.test.tsx).
  testIdPrefix: 'employee-ext',
  coreFieldSlugs: Object.keys(EMPLOYEE_CORE_FIELD_SLUGS),
  fields,
  coerceEmptyToNull: false,
};
